import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { SearchClear, PcSelect } from '../_Components';
import { GET_LIST, provider, history } from '../_utils';
import Utils from './components/utils';
import EditOperatorModal from './components/Show/EditOperatorModal';
import {
    TYPE_META,
    deptLabel,
    fmtDateUtc,
    fmtPhone,
    fullName,
    initials,
    isActive,
    mobilePhone,
    operatorId,
    statusMeta,
    telegram,
    telegramLinked,
    typeLabel,
} from './format';
import './operators.css';

const { operatorsTypes, departments, contractTypes } = Utils.options();

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const SORT_FIELD = {
    name: 'fullname',
    type: 'type',
    dept: 'department',
    contract: 'contractType',
    contractEnd: 'contractEnd',
    active: 'status',
};

const contractTypeLabel = (row) => {
    const id = row?.contractType || row?.contract?.type;
    return contractTypes.find((item) => item.id === id)?.name || '';
};

const contractEndValue = (row) => row?.contractEnd || row?.contract?.end;

const LIST_STATE_KEY = 'pc-operators-list';
const SORT_KEYS = Object.keys(SORT_FIELD);
const PER_PAGES = [25, 50, 100];

const readListState = () => {
    try {
        return JSON.parse(sessionStorage.getItem(LIST_STATE_KEY)) || {};
    } catch (error) {
        return {};
    }
};

const writeListState = (state) => {
    try {
        sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify(state));
    } catch (error) {
        /* ignore quota / private mode */
    }
};

const OperatorsList = () => {
    const saved = readListState();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState(typeof saved.query === 'string' ? saved.query : '');
    const [debouncedQuery, setDebouncedQuery] = useState(typeof saved.query === 'string' ? saved.query.trim() : '');
    const [type, setType] = useState(typeof saved.type === 'string' ? saved.type : '');
    const [dept, setDept] = useState(typeof saved.dept === 'string' ? saved.dept : '');
    const [act, setAct] = useState(typeof saved.act === 'string' ? saved.act : 'Active');
    const [sortKey, setSortKey] = useState(SORT_KEYS.includes(saved.sortKey) ? saved.sortKey : 'name');
    const [sortDir, setSortDir] = useState(saved.sortDir === -1 ? -1 : 1);
    const [page, setPage] = useState(Number(saved.page) > 0 ? Number(saved.page) : 1);
    const [perPage, setPerPage] = useState(PER_PAGES.includes(Number(saved.perPage)) ? Number(saved.perPage) : 25);
    const [openCreate, setOpenCreate] = useState(false);
    const skipPageReset = useRef(true);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const load = useCallback(async () => {
        const filter = {};
        if (act) filter.status = act;
        if (type) filter.type = type;
        if (dept) filter.department = dept;
        if (debouncedQuery) {
            if (/^[a-z]+\d*$/i.test(debouncedQuery)) filter.login = debouncedQuery;
            else filter.fullname = debouncedQuery;
        }

        try {
            const { data, total: count } = await provider(GET_LIST, '/operators', {
                filter,
                sort: { field: SORT_FIELD[sortKey] || 'fullname', order: sortDir > 0 ? 'ASC' : 'DESC' },
                pagination: { page, perPage },
            });
            setRows(data || []);
            setTotal(count || 0);
        } catch (error) {
            console.error('Error loading operators:', error);
        }
    }, [act, type, dept, debouncedQuery, sortKey, sortDir, page, perPage]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        writeListState({ query, type, dept, act, sortKey, sortDir, page, perPage });
    }, [query, type, dept, act, sortKey, sortDir, page, perPage]);

    useEffect(() => {
        if (skipPageReset.current) {
            skipPageReset.current = false;
            return;
        }
        setPage(1);
    }, [act, type, dept, debouncedQuery, sortKey, sortDir, perPage]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir((dir) => -dir);
        else { setSortKey(key); setSortDir(1); }
    };

    const sortClass = (key) => (sortKey === key ? 's' : '');
    const arrow = sortDir > 0 ? '↑' : '↓';

    return (
        <Fragment>
            <div className="pc-page pc-emp">
                <div className="pc-emp-topbar">
                    <h1>Сотрудники</h1>
                    <div className="pc-emp-srch">
                        <Icon d="M7 2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zM10.5 10.5L14 14" />
                        <input
                            className="pc-emp-inp"
                            value={query}
                            placeholder="Поиск по имени, логину или телефону"
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <SearchClear visible={Boolean(query)} onClear={() => setQuery('')} />
                    </div>
                    <PcSelect
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        options={operatorsTypes}
                        allowEmpty
                        emptyLabel="Все типы"
                        searchable={false}
                    />
                    <PcSelect
                        value={dept}
                        onChange={(e) => setDept(e.target.value)}
                        options={departments}
                        allowEmpty
                        emptyLabel="Все подразделения"
                        searchable={false}
                    />
                    <PcSelect
                        value={act}
                        onChange={(e) => setAct(e.target.value)}
                        options={[
                            { id: 'Active', name: 'Активные' },
                            { id: 'Terminated', name: 'Отключённые' },
                        ]}
                        allowEmpty
                        emptyLabel="Все статусы"
                        searchable={false}
                    />
                    <span className="pc-emp-chip pc-emp-chip--mono">
                        {total
                            ? `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} из ${total}`
                            : '0 сотрудников'}
                    </span>
                    <div className="pc-emp-end">
                        <button type="button" className="pc-emp-btn" onClick={() => history.push('/operators/reports')}>
                            <Icon d="M4 2h8v12H4zM6.5 5.5h3M6.5 8h3M6.5 10.5h2" />
                            Отчеты
                        </button>
                        <button type="button" className="pc-emp-btn pc-emp-btn--pri" onClick={() => setOpenCreate(true)}>
                            <Icon d="M8 3.5v9M3.5 8h9" />
                            Новый сотрудник
                        </button>
                    </div>
                </div>

                <div className="pc-emp-stage">
                    <div className="pc-emp-panel">
                        <div className="pc-emp-thead">
                            <span className={`pc-emp-c-emp ${sortClass('name')}`} data-sort="name" onClick={() => toggleSort('name')}>
                                Сотрудник<span className="ar">{arrow}</span>
                            </span>
                            <span className={`pc-emp-c-type ${sortClass('type')}`} data-sort="type" onClick={() => toggleSort('type')}>
                                Тип<span className="ar">{arrow}</span>
                            </span>
                            <span className={`pc-emp-c-dept ${sortClass('dept')}`} data-sort="dept" onClick={() => toggleSort('dept')}>
                                Подразделение<span className="ar">{arrow}</span>
                            </span>
                            <span className={`pc-emp-c-ctr ${sortClass('contract')}`} data-sort="contract" onClick={() => toggleSort('contract')}>
                                Договор<span className="ar">{arrow}</span>
                            </span>
                            <span className={`pc-emp-c-cend ${sortClass('contractEnd')}`} data-sort="contractEnd" onClick={() => toggleSort('contractEnd')}>
                                До<span className="ar">{arrow}</span>
                            </span>
                            <span className="pc-emp-c-phone">Телефон</span>
                            <span className="pc-emp-c-tg">Telegram</span>
                            <span className={`pc-emp-c-st ${sortClass('active')}`} data-sort="active" onClick={() => toggleSort('active')}>
                                Статус<span className="ar">{arrow}</span>
                            </span>
                            <span className="pc-emp-c-go" />
                        </div>
                        <div className="pc-emp-pbd">
                            {rows.length === 0 ? (
                                <div className="pc-emp-empty">
                                    <b>Ничего не найдено</b>
                                    <p>Измените запрос или сбросьте фильтры.</p>
                                </div>
                            ) : rows.map((row) => {
                                const id = operatorId(row);
                                const name = fullName(row);
                                const meta = TYPE_META[row.type] || TYPE_META.staf;
                                const status = statusMeta(row);
                                const phone = mobilePhone(row);
                                const tg = telegram(row);
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        className={`pc-emp-trow${isActive(row) ? '' : ' off'}`}
                                        onClick={() => history.push(`/operators/${id}/show`)}
                                    >
                                        <span className="pc-emp-c-emp">
                                            <span className={`pc-emp-av pc-emp-av-${meta.av}`}>{initials(name)}</span>
                                            <span style={{ minWidth: 0 }}>
                                                <span className="n">{name || '—'}</span>
                                                <span className="s">{row.login || '—'}</span>
                                            </span>
                                        </span>
                                        <span className="pc-emp-c-type">
                                            <span className={`pc-emp-chip ${meta.chip}`}>{typeLabel(row.type)}</span>
                                        </span>
                                        <span className="pc-emp-c-dept">{deptLabel(row.department) || <span className="pc-emp-dim">—</span>}</span>
                                        <span className="pc-emp-c-ctr">
                                            {contractTypeLabel(row) || <span className="pc-emp-dim">—</span>}
                                        </span>
                                        <span className="pc-emp-c-cend">
                                            {contractEndValue(row)
                                                ? fmtDateUtc(contractEndValue(row))
                                                : <span className="pc-emp-dim">—</span>}
                                        </span>
                                        <span className="pc-emp-c-phone">
                                            {phone ? fmtPhone(phone) : <span className="pc-emp-dim">—</span>}
                                        </span>
                                        <span className="pc-emp-c-tg">
                                            {telegramLinked(row) && tg
                                                ? tg
                                                : <span className="pc-emp-dim">не привязан</span>}
                                        </span>
                                        <span className="pc-emp-c-st">
                                            <span className={`pc-emp-chip ${status.chip}`}>
                                                <span className="pc-emp-dot" />
                                                {status.label}
                                            </span>
                                        </span>
                                        <span className="pc-emp-c-go">
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                                <path d="M6 3.5L10.5 8 6 12.5" />
                                            </svg>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="pc-emp-pager">
                            <PcSelect
                                value={String(perPage)}
                                onChange={(e) => setPerPage(Number(e.target.value))}
                                options={[25, 50, 100].map((n) => ({ id: String(n), name: `${n} на странице` }))}
                                searchable={false}
                            />
                            <span className="pc-emp-pager__n">
                                {total
                                    ? `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} из ${total}`
                                    : '0 из 0'}
                            </span>
                            <span className="pc-emp-pager__n">{page} / {Math.max(1, Math.ceil(total / perPage))}</span>
                            <div className="pc-emp-navb">
                                <button
                                    type="button"
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    title="Назад"
                                >
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M10 3.5L5.5 8l4.5 4.5" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    disabled={page >= Math.max(1, Math.ceil(total / perPage))}
                                    onClick={() => setPage((p) => p + 1)}
                                    title="Вперёд"
                                >
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M6 3.5L10.5 8 6 12.5" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <EditOperatorModal
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onSave={load}
            />
        </Fragment>
    );
};

export default OperatorsList;
